import { Information } from '@carbon/icons-react';
import type { SelectOption } from '@ph-react-ui/core';
import { Button, useNotifications } from '@ph-react-ui/core';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useCreatePaymentScheme } from '../../hooks/payment-schemes/useCreateScheme';
import { useGetPaymentSchemeById } from '../../hooks/payment-schemes/useGetSchemeById';
import { useUpdatePaymentSchemeById } from '../../hooks/payment-schemes/useUpdateScheme';
import type { PaymentSchemeEditDto } from '../../models/payment-schemes/payment-scheme-edit.dto';
import { Input, Select } from '../../utils/components/hoc-components';
import { PartOfSchoolYear } from '../../utils/enums/payment-scheme-school-year-half.enum';
import {
  getCommonValidators,
  VALIDATION as V,
  createNumberValidator,
  createMinValueValidator,
  createMaxValueValidator,
  createIntegerValidator,
} from '../../utils/inputValidators';
import { CancellationDialog } from '../shared/components/CancellationDialog';
import { LabelWithInfo } from '../shared/components/LabelWithInfoIcon';
import { FormSkeleton } from '../shared/components/skeletons/FormSkeleton';

type PaymentSchemeFormValues = {
  name: string;
  yearlyPrice: string;
  installmentCount: number;
  templateSchemeEntries: {
    amount: string;
    dueMonth: number | null;
    dueDay: number | null;
    penalty: string;
    partOfSchoolYear: PartOfSchoolYear;
  }[];
  gracePeriod: string;
  discount: string;
  penaltyAmountInput?: number;
};

export const CreateEditPaymentScheme = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const notification = useNotifications();

  const schemeId = id ?? null;
  const [isCancellationDialogOpen, setIsCancellationDialogOpen] =
    useState(false);

  const { data: schemeData, isLoading } = useGetPaymentSchemeById(
    schemeId,
    Boolean(schemeId)
  );

  const createScheme = useCreatePaymentScheme();
  const updateScheme = useUpdatePaymentSchemeById(schemeId);

  const isFormDisabled = Boolean(schemeId && schemeData && !schemeData.canEdit);

  const methods = useForm<PaymentSchemeFormValues>({
    mode: 'all',
    reValidateMode: 'onChange',
    defaultValues: {
      name: '',
      yearlyPrice: '',
      installmentCount: 0,
      templateSchemeEntries: [],
      gracePeriod: '',
      discount: '',
      penaltyAmountInput: undefined,
    },
  });

  const { fields, replace } = useFieldArray({
    name: 'templateSchemeEntries',
    control: methods.control,
  });

  const [
    installmentCount,
    schemesWatch,
    yearlyPriceWatch,
    discountWatch,
    penaltyAmountInputWatch,
    nameWatch,
    gracePeriodValue,
  ] = methods.watch([
    'installmentCount',
    'templateSchemeEntries',
    'yearlyPrice',
    'discount',
    'penaltyAmountInput',
    'name',
    'gracePeriod',
  ]);

  const { isDirty } = methods.formState;
  const isPerformingApiRequest =
    createScheme.isPending || updateScheme.isPending;

  const numberRules = useMemo(
    () => ({
      isNumber: createNumberValidator(t),
      minValue: createMinValueValidator(t, 0, true),
    }),
    [t]
  );

  const percentageRules = useMemo(
    () => ({
      ...numberRules,
      maxValue: createMaxValueValidator(t, 100),
    }),
    [numberRules, t]
  );

  const installmentCountOptions = useMemo(
    () =>
      Array.from({ length: 20 }, (_, i) => ({
        label: `${i + 1}`,
        value: i + 1,
      })),
    []
  );

  const dayOptions = useMemo(
    () =>
      Array.from({ length: 31 }, (_, i) => ({
        label: `${i + 1}`,
        value: i + 1,
      })),
    []
  );

  const monthOptions = useMemo(() => {
    const monthKeys = [
      'JANUARY',
      'FEBRUARY',
      'MARCH',
      'APRIL',
      'MAY',
      'JUNE',
      'JULY',
      'AUGUST',
      'SEPTEMBER',
      'OCTOBER',
      'NOVEMBER',
      'DECEMBER',
    ];
    return monthKeys.map((key, index) => {
      const monthName = t(`MONTHS.${key}`);
      return {
        label: monthName.toLowerCase(),
        value: index + 1,
      };
    });
  }, [t]);

  const schoolYearHalfOptions = useMemo<SelectOption<number | string>[]>(
    () => [
      {
        label: t('PAYMENT_SCHEMES.DRAWER.LABELS.FIRST_HALF'),
        value: PartOfSchoolYear.firstHalf,
      },
      {
        label: t('PAYMENT_SCHEMES.DRAWER.LABELS.SECOND_HALF'),
        value: PartOfSchoolYear.secondHalf,
      },
    ],
    [t]
  );

  useEffect(() => {
    if (schemeData) {
      const formData = {
        name: schemeData.name,
        yearlyPrice: schemeData.yearlyPrice.toString(),
        installmentCount: schemeData.templateSchemeEntries.length,
        templateSchemeEntries: schemeData.templateSchemeEntries.map((e) => ({
          amount: e.amount.toString(),
          dueMonth: e.dueMonth || null,
          dueDay: e.dueDay || null,
          penalty: e.penalty.toString(),
          partOfSchoolYear: e.partOfSchoolYear,
        })),
        gracePeriod: schemeData.gracePeriod.toString(),
        discount: schemeData.discount.toString(),
        penaltyAmountInput: schemeData.templateSchemeEntries[1]?.penalty || 0,
      };
      methods.reset(formData);
    }
  }, [methods, schemeData]);

  // Sync installment fields with the installment count
  useEffect(() => {
    const targetLength = installmentCount ?? 0;

    if (targetLength !== fields.length) {
      const currentValues = methods.getValues('templateSchemeEntries');
      const newEntries = Array.from({ length: targetLength }, (_, index) => ({
        amount: currentValues[index]?.amount ?? '',
        dueMonth: currentValues[index]?.dueMonth ?? 1,
        dueDay: currentValues[index]?.dueDay ?? 1,
        penalty: currentValues[index]?.penalty ?? '',
        partOfSchoolYear: currentValues[index]?.partOfSchoolYear ?? '',
      }));
      replace(newEntries);
    }
  }, [installmentCount, fields.length, replace, methods]);

  // Sync penalty amount to all installments except the first one
  useEffect(() => {
    if (penaltyAmountInputWatch !== undefined) {
      fields.forEach((_, index) => {
        const penalty = index === 0 ? '0' : penaltyAmountInputWatch.toString();
        methods.setValue(`templateSchemeEntries.${index}.penalty`, penalty, {
          shouldValidate: true,
        });
      });
    }
  }, [penaltyAmountInputWatch, fields, methods]);

  const totalPercentage = schemesWatch.reduce(
    (sum, entry) => sum + (Number(entry?.amount) || 0),
    0
  );
  const isAmountCorrect = totalPercentage === 100;
  const hasErrors = Object.keys(methods.formState.errors).length > 0;

  const areRequiredFieldsFilled =
    nameWatch &&
    yearlyPriceWatch &&
    gracePeriodValue &&
    penaltyAmountInputWatch != null &&
    installmentCount > 0;

  const isSubmitDisabled =
    !isDirty ||
    hasErrors ||
    !areRequiredFieldsFilled ||
    !isAmountCorrect ||
    !schemesWatch.every(
      (entry) => entry?.amount && entry?.partOfSchoolYear != null
    ) ||
    isPerformingApiRequest ||
    isFormDisabled;

  const createLabelWithInfo = (
    labelKey: string,
    tooltipKey: string,
    index?: number
  ) => ({
    label: (
      <LabelWithInfo
        label={
          index !== undefined
            ? `${t(`PAYMENT_SCHEMES.DRAWER.LABELS.${labelKey}`)} ${index + 1}`
            : t(`PAYMENT_SCHEMES.DRAWER.LABELS.${labelKey}`)!
        }
        tooltip={t(`PAYMENT_SCHEMES.DRAWER.TOOLTIPS.${tooltipKey}`)}
      />
    ) as any,
  });

  const onDiscardChanges = () => {
    if (isDirty) {
      setIsCancellationDialogOpen(true);
    } else {
      navigate('/dashboard/payment-schemes');
    }
  };

  const handleMutation = async (
    data: PaymentSchemeEditDto,
    isUpdate: boolean
  ) => {
    const mutation = isUpdate ? updateScheme : createScheme;

    try {
      await mutation.mutateAsync(data);

      const queries = isUpdate
        ? [
            { queryKey: ['payment-schemes', schemeId] },
            { queryKey: ['payment-schemes'] },
          ]
        : [{ queryKey: ['payment-schemes'] }];

      queries.forEach((query) => queryClient.invalidateQueries(query));

      notification.success(
        t(
          isUpdate
            ? 'PAYMENT_SCHEMES.DRAWER.MESSAGES.SCHEME_UPDATED'
            : 'PAYMENT_SCHEMES.DRAWER.MESSAGES.SCHEME_CREATED'
        )
      );

      methods.reset(methods.getValues());
    } catch (resError: any) {
      notification.danger(t(`NETWORK_ERRORS.${resError.errors[0]}`));
    }
  };

  const onSubmit = async (data: PaymentSchemeFormValues) => {
    const dto: PaymentSchemeEditDto = {
      name: data.name,
      yearlyPrice: Number(data.yearlyPrice),
      discount: Number(data.discount) || 0,
      gracePeriod: Number(data.gracePeriod),
      templateSchemeEntries: data.templateSchemeEntries.map((e) => ({
        amount: Number(e.amount),
        dueMonth: Number(e.dueMonth),
        dueDay: Number(e.dueDay),
        penalty: Number(e.penalty),
        partOfSchoolYear: e.partOfSchoolYear,
      })),
    };

    await handleMutation(dto, Boolean(schemeId));
  };

  if (isLoading && schemeId) {
    return <FormSkeleton />;
  }

  return (
    <div className="create-edit-payment-scheme py-4 px-8">
      <div className="create-edit-payment-scheme__header mb-8">
        <h1 className="create-edit-payment-scheme__title">
          {schemeId
            ? t('PAYMENT_SCHEMES.DRAWER.TITLES.EDIT_SCHEME')
            : t('PAYMENT_SCHEMES.DRAWER.TITLES.CREATE_SCHEME')}
        </h1>
      </div>

      <FormProvider {...methods}>
        <form
          id="payment-scheme-form"
          className="create-edit-payment-scheme__form"
          onSubmit={methods.handleSubmit(onSubmit)}
        >
          <div className="create-edit-payment-scheme__form__financial-fields mb-6">
            <Input
              name="name"
              label={t('PAYMENT_SCHEMES.DRAWER.LABELS.NAME')!}
              placeholder={t('PAYMENT_SCHEMES.DRAWER.PLACEHOLDERS.NAME')!}
              rules={getCommonValidators([V.REQUIRED, V.MAX_LENGTH_255])}
              disabled={isFormDisabled}
            />

            <Input
              name="yearlyPrice"
              label={`${t('PAYMENT_SCHEMES.DRAWER.LABELS.TOTAL_AMOUNT')} (EUR)`}
              placeholder={
                t('PAYMENT_SCHEMES.DRAWER.PLACEHOLDERS.TOTAL_AMOUNT')!
              }
              rules={{
                ...getCommonValidators([V.REQUIRED]),
                validate: {
                  isNumber: createNumberValidator(t),
                  minValue: createMinValueValidator(t, 1, true),
                },
              }}
              disabled={isFormDisabled}
            />
          </div>

          <div className="create-edit-payment-scheme__form__financial-fields mb-6">
            <Input
              name="discount"
              label={`${t('PAYMENT_SCHEMES.DRAWER.LABELS.DISCOUNT')} (EUR)`}
              placeholder={t('PAYMENT_SCHEMES.DRAWER.PLACEHOLDERS.DISCOUNT')!}
              rules={{
                validate: {
                  isNumber: (value: string) =>
                    !value || createNumberValidator(t)(value),
                  minValue: (value: string) =>
                    !value || createMinValueValidator(t, 0, true)(value),
                  maxDiscount: (value: string) =>
                    !value ||
                    Number(value) <= Number(yearlyPriceWatch) ||
                    (t('SETTINGS.ERRORS.GREATER_THAN_FULL_AMOUNT') as string),
                },
              }}
              disabled={isFormDisabled}
            />

            <Input
              name="gracePeriod"
              label={t('PAYMENT_SCHEMES.DRAWER.LABELS.GRACE_PERIOD')!}
              placeholder={
                t('PAYMENT_SCHEMES.DRAWER.PLACEHOLDERS.GRACE_PERIOD')!
              }
              rules={{
                ...getCommonValidators([V.REQUIRED]),
                validate: {
                  ...numberRules,
                  isInteger: createIntegerValidator(t),
                },
              }}
              disabled={isFormDisabled}
            />
          </div>

          <div className="create-edit-payment-scheme__form__financial-fields mb-6">
            <Input
              name="penaltyAmountInput"
              label={t('PAYMENT_SCHEMES.DRAWER.LABELS.LATE_FEE_SHORT')!}
              placeholder={t('PAYMENT_SCHEMES.DRAWER.PLACEHOLDERS.LATE_FEE')!}
              rules={{
                ...getCommonValidators([V.REQUIRED]),
                validate: percentageRules,
              }}
              disabled={isFormDisabled}
            />

            <Select
              name="installmentCount"
              searchable={true}
              label={t('PAYMENT_SCHEMES.DRAWER.LABELS.NUMBER_OF_INSTALLMENTS')!}
              placeholder={
                t('PAYMENT_SCHEMES.DRAWER.PLACEHOLDERS.NUMBER_OF_INSTALLMENTS')!
              }
              options={installmentCountOptions}
              rules={getCommonValidators([V.REQUIRED])}
              disabled={isFormDisabled}
            />
          </div>

          {fields.length > 0 && (
            <div className="create-edit-payment-scheme__form__installments mb-4">
              {fields.map((field, index) => {
                const isLastInstallment = index === fields.length - 1;

                const percentage = Number(schemesWatch[index]?.amount) || 0;
                const penaltyPercentage =
                  Number(schemesWatch[index]?.penalty) || 0;
                const yearlyPrice = Number(yearlyPriceWatch) || 0;
                const discount = Number(discountWatch) || 0;

                const calculatedAmount =
                  (percentage / 100) * (yearlyPrice - discount);
                const penaltyAmount = (penaltyPercentage / 100) * yearlyPrice;
                const amountWithPenalty = calculatedAmount + penaltyAmount;

                return (
                  <div
                    key={field.id}
                    className={`create-edit-payment-scheme__form__installment ${
                      !isLastInstallment
                        ? 'create-edit-payment-scheme__form__installment--with-separator'
                        : ''
                    }`}
                  >
                    <Input
                      name={`templateSchemeEntries.${index}.amount`}
                      {...createLabelWithInfo(
                        'INSTALLMENT_SHORT',
                        'INSTALLMENT',
                        index
                      )}
                      placeholder="0"
                      rules={{
                        ...getCommonValidators([V.REQUIRED]),
                        validate: percentageRules,
                      }}
                      disabled={isFormDisabled}
                    />

                    <Select
                      name={`templateSchemeEntries.${index}.partOfSchoolYear`}
                      {...createLabelWithInfo(
                        'SCHOOL_YEAR_HALF',
                        'SCHOOL_YEAR_HALF'
                      )}
                      placeholder={
                        t('PAYMENT_SCHEMES.DRAWER.PLACEHOLDERS.CHOOSE_HALF')!
                      }
                      options={schoolYearHalfOptions}
                      rules={getCommonValidators([V.REQUIRED])}
                      disabled={isFormDisabled}
                    />

                    <Input
                      name={`calculatedAmount_${index}`}
                      {...createLabelWithInfo(
                        'CALCULATED_AMOUNT_SHORT',
                        'CALCULATED_AMOUNT'
                      )}
                      value={calculatedAmount.toFixed(2)}
                      disabled
                      className="create-edit-payment-scheme__form__installment__calculated"
                    />

                    <Select
                      name={`templateSchemeEntries.${index}.dueDay`}
                      {...createLabelWithInfo(
                        'DEADLINE_DAY_SHORT',
                        'DEADLINE_DAY'
                      )}
                      searchable={true}
                      options={dayOptions}
                      rules={getCommonValidators([V.REQUIRED])}
                      disabled={isFormDisabled}
                    />

                    <Select
                      name={`templateSchemeEntries.${index}.dueMonth`}
                      {...createLabelWithInfo(
                        'DEADLINE_MONTH_SHORT',
                        'DEADLINE_MONTH'
                      )}
                      searchable={true}
                      options={monthOptions}
                      rules={getCommonValidators([V.REQUIRED])}
                      disabled={isFormDisabled}
                    />

                    <Input
                      name={`templateSchemeEntries.${index}.penalty`}
                      {...createLabelWithInfo('LATE_FEE_SHORT', 'LATE_FEE')}
                      placeholder={
                        t('PAYMENT_SCHEMES.DRAWER.PLACEHOLDERS.LATE_FEE')!
                      }
                      disabled
                    />

                    <Input
                      name={`amountWithPenalty_${index}`}
                      {...createLabelWithInfo(
                        'AMOUNT_WITH_PENALTY_SHORT',
                        'AMOUNT_WITH_PENALTY'
                      )}
                      value={amountWithPenalty.toFixed(2)}
                      disabled
                      className="create-edit-payment-scheme__form__installment__calculated"
                    />
                  </div>
                );
              })}
            </div>
          )}

          {installmentCount > 0 && (
            <div
              className={`create-edit-payment-scheme__form__total p-4 mb-5 ${
                totalPercentage > 100
                  ? 'create-edit-payment-scheme__form__total--error'
                  : ''
              }`}
            >
              <span className="create-edit-payment-scheme__form__total__text">
                {t('PAYMENT_SCHEMES.DRAWER.LABELS.TOTAL_PERCENTAGE')}:{' '}
                <strong>{totalPercentage}%</strong>
              </span>
            </div>
          )}

          <div className="create-edit-payment-scheme__form__actions">
            {isFormDisabled && (
              <div className="display-flex-row align-items-center gap-1 text-muted">
                <Information size={16} />
                <span className="text-sm">
                  {t('PAYMENT_SCHEMES.DRAWER.TOOLTIPS.SCHEME_IN_USE')}
                </span>
              </div>
            )}
            <div className="create-edit-payment-scheme__form__actions__buttons">
              <Button
                onClick={onDiscardChanges}
                type="button"
                variant="outlined"
              >
                {t('ACTIONS.BACK')}
              </Button>
              <Button
                type="submit"
                loading={isPerformingApiRequest}
                disabled={isSubmitDisabled}
              >
                {schemeId ? t('ACTIONS.UPDATE') : t('ACTIONS.SAVE')}
              </Button>
            </div>
          </div>
        </form>
      </FormProvider>
      {isCancellationDialogOpen && (
        <CancellationDialog
          onDiscardChanges={() => navigate('/dashboard/payment-schemes')}
          onClose={() => setIsCancellationDialogOpen(false)}
        />
      )}
    </div>
  );
};
